import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/store/index';
import { useGetCategoriesResourcesQuery } from '@/store/apiSlice';
import { Category } from '@/types';

export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<RootState>();

/**
 * Hook to get combined categories (categories + resources)
 * The endpoint now returns categories with nested resources, pre-sorted
 */
export const useCategoriesResources = () => {
    const { data: categoryResourcesData } = useGetCategoriesResourcesQuery();

    return useMemo(() => {
        if (!categoryResourcesData) return [];

        // Data is already structured and sorted by the backend
        return categoryResourcesData.map((cat: any) => ({
            id: cat.id,
            name: cat.name,
            group: cat.group,
            icon: cat.icon ?? '',
            active: cat.active,
            sortPriority: cat.sortPriority,
            allowMultiple: cat.allowMultiple,
            minAssignments: cat.minAssignments,
            maxAssignments: cat.maxAssignments,
            notifyDaysBeforeWhenUnassigned: cat.notifyDaysBeforeWhenUnassigned ?? null,
            resources: cat.resources || [],
        })) as Category[];
    }, [categoryResourcesData]);
};

/**
 * Hook to get all categories (regular + ChurchTools services)
 * ChurchTools services are automatically extracted when agenda queries run
 */
export const useCategories = () => {
    const categories = useCategoriesResources();

    const churchToolsServices = useAppSelector((state) => state.cache.churchToolsServices);

    return useMemo(
        () => [
            ...categories,
            ...churchToolsServices.map(
                (service) =>
                    ({
                        id: 0,
                        name: service,
                        group: 'ChurchTools',
                        icon: 'churchtools',
                        active: 1,
                        resources: [],
                    }) as Category
            ),
        ],
        [categories, churchToolsServices]
    );
};
